import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner';
import { redirectToLogin } from '@/services/auth/authUtils';
import { cn } from '@/lib/utils';

interface LoginFormProps {
  className?: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({ className }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = () => {
    try {
      setIsLoading(true);
      setError(null);
      redirectToLogin();
    } catch (err) {
      setIsLoading(false);
      setError(err instanceof Error ? err.message : 'Failed to redirect to login');
    }
  };

  return (
    <Card className={cn('max-w-md mx-auto shadow-lg border-0 bg-white', className)}>
      <CardHeader className="space-y-2 text-center">
        <CardTitle className="text-2xl font-bold">Log in to your account</CardTitle>
        <CardDescription className="text-base text-muted-foreground">
          Log in to access the AskiiMall marketplace.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4">
        <Button
          type="button"
          size="lg"
          variant="outline"
          className="w-full flex items-center justify-center gap-2 font-semibold text-base h-12 bg-white border-gray-300 hover:bg-gray-50"
          onClick={handleLogin}
          disabled={isLoading}
          aria-busy={isLoading}
        >
          {isLoading ? (
            <LoadingSpinner size="sm" text="Redirecting..." />
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6 mr-3">
                <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clipRule="evenodd"></path>
              </svg>
              <span>Continue with mPass</span>
            </>
          )}
        </Button>
      </CardContent>
      {error && (
        <CardFooter className="flex flex-col items-center pt-0">
          <div
            className="w-full rounded-md bg-red-50 text-red-600 text-sm px-4 py-2 text-center border border-red-200"
            aria-live="polite"
          >
            {error}
          </div>
        </CardFooter>
      )}
    </Card>
  );
};
